import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {
  Product,
  CategoryOption,
  UnitOption,
  createProduct,
  editProduct,
  getCatalogueOptions,
} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const productSchema = Yup.object().shape({
  product_name: Yup.string()
    .required('Product name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,100}$/, {
      message:
        'Product name must be 1-100 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  sku_code: Yup.string()
    .required('SKU Code is required')
    .matches(/^[a-zA-Z0-9\-_' ]{1,50}$/, {
      message:
        'SKU Code must be 1-50 characters and can only contain letters, numbers, hyphens and underscores',
    }),
  category: Yup.string().required('Category is required'),
  unit: Yup.string().required('Unit is required'),
  price_per_unit: Yup.number().min(0, 'Price must be 0 or greater').required('Price is required'),
  description: Yup.string(),
  status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProductFormValues {
  product_name: string
  sku_code: string
  category: string
  unit: string
  price_per_unit: number
  description: string
  status: string
}

type OptionType = {
  value: string
  label: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateEditProduct: React.FC<{
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>
  setEditProduct: React.Dispatch<React.SetStateAction<Product | undefined>>
  editProductData?: Product
  setShouldRefreshProducts: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setShowForm, editProductData, setShouldRefreshProducts, setEditProduct}) => {
  const {company} = useLayout()

  const [loading, setLoading] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState<OptionType[]>([])
  const [unitOptions, setUnitOptions] = useState<OptionType[]>([])
  const [category, setCategory] = useState<OptionType>({
    label: 'Select Category...',
    value: '',
  })
  const [unit, setUnit] = useState<OptionType>({
    label: 'Select Unit...',
    value: '',
  })

  const initialValues: ProductFormValues = {
    product_name: editProductData?.product_name || '',
    sku_code: editProductData?.sku_code || '',
    category: editProductData?.category || '',
    unit: editProductData?.unit || '',
    price_per_unit: editProductData?.price_per_unit ?? 0,
    description: editProductData?.description || '',
    status: editProductData?.status || 'active',
  }

  const formik = useFormik<ProductFormValues>({
    initialValues,
    validationSchema: productSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        product_id: editProductData?.product_id || null,
        company_id: company?.value,
        product_name: values.product_name,
        sku_code: values.sku_code,
        category: category.value,
        unit: unit.value,
        price_per_unit: values.price_per_unit,
        description: values.description,
        status: values.status,
      }

      if (!editProductData?.product_id) {
        await createProduct(body)
          .then((res) => {
            res?.detail && toast.success('Product has been added successfully.')
            formik.resetForm()
            setShouldRefreshProducts(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      } else {
        await editProduct(body)
          .then((res) => {
            res?.detail && toast.success('Product has been updated successfully.')
            formik.resetForm()
            setShouldRefreshProducts(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      }
    },
  })

  // Load category and unit options for the dropdowns
  useEffect(() => {
    const loadOptions = async () => {
      if (!company?.value) return

      const res = await getCatalogueOptions(company.value)
      if (res) {
        setCategoryOptions(
          res.categories.map((c: CategoryOption) => ({
            label: c.category_name,
            value: c.category_name,
          }))
        )
        setUnitOptions(
          res.units.map((u: UnitOption) => ({
            label: u.unit_name,
            value: u.unit_name,
          }))
        )
      }
    }

    loadOptions()
  }, [company?.value])

  // Pre-populate selects when editing
  useEffect(() => {
    if (editProductData) {
      setCategory({label: editProductData.category, value: editProductData.category})
      setUnit({label: editProductData.unit, value: editProductData.unit})
    }
  }, [editProductData])

  // Sync select state back into formik for validation
  useEffect(() => {
    formik.setFieldValue('category', category.value, true)
    formik.setFieldValue('unit', unit.value, true)
  }, [category.value, unit.value])

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_product_catalogue_form'
        aria-expanded='true'
        aria-controls='kt_product_catalogue_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            {!!editProductData ? 'Update Product' : 'Add New Product'}
          </h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_product_catalogue_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Product Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Product Name</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Product Name'
                  {...formik.getFieldProps('product_name')}
                />
                {formik.touched.product_name && formik.errors.product_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.product_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* SKU Code */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>SKU Code</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='SKU Code'
                  {...formik.getFieldProps('sku_code')}
                />
                {formik.touched.sku_code && formik.errors.sku_code && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.sku_code}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Category */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Category</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={category}
                  onChange={(option: any) => setCategory(option as OptionType)}
                  options={categoryOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Unit */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Unit</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={unit}
                  onChange={(option: any) => setUnit(option as OptionType)}
                  options={unitOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Price per Unit */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Price / Unit (&#8377;)
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Price per Unit'
                  {...formik.getFieldProps('price_per_unit')}
                />
                {formik.touched.price_per_unit && formik.errors.price_per_unit && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.price_per_unit}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Description</label>
              <div className='col-lg-9 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Description'
                  rows={3}
                  {...formik.getFieldProps('description')}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.description}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Status</label>
              <div className='col-lg-9 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('status')}
                >
                  <option value='active'>Active</option>
                  <option value='inactive'>Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setEditProduct(undefined)
              setShowForm(false)
              setShouldRefreshProducts(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading && !!editProductData ? 'Save Product' : 'Add Product'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
